import React, { useState } from "react"

import { action, makeAutoObservable, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { joinClasses, changeView, useClasses } from "../globals";
import { signOut, signIn, register } from "../simgate";
import { PasswordHint, isPasswordValid } from "./password-hint";

import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"

// Icons
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import AutorenewIcon from "@material-ui/icons/Autorenew"
import VisibilityIcon from "@material-ui/icons/Visibility"
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff"


class SignUpStore {

  processing = false;

  email = "";
  emailError = "";
  showEmailHint = false;

  firstName = "";
  firstNameError = "";

  lastName = "";
  lastNameError = "";

  password = "";
  passwordError = "";
  passwordVisible = false;
  passwordHintVisible = false;

  passwordConfirm = "";
  passwordConfirmError = "";

  serverError = "";

  constructor(app, email = "") {
    this.email = email;
    makeAutoObservable(this);
    this.app = app;
  }

  async createUser() {

    let cont = true;
    const { email, firstName, lastName, password, passwordConfirm } = this;

    runInAction(() => {
      this.processing = true;
      this.emailError = "";
      this.passwordError = "";
      this.passwordConfirmError = "";
      this.serverError = "";
      this.firstNameError = "";
      this.lastNameError = "";

      if (!email) {
        this.emailError = "Email Address is required";
      }
      else if (!/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(email)) {
        this.emailError = "Email Address is invalid";
      }

      if (!password) {
        this.passwordError = "Password is required";
      }
      else if (!isPasswordValid(password)) {
        this.passwordError = "Password does not meet all the requirements";
      }
      if (password !== passwordConfirm) {
        this.passwordConfirmError = "Passwords do not match";
      }

      if (!firstName) {
        this.firstNameError = "First Name is required";
      }
      else if (firstName.length < 2 || firstName.length > 64) {
        this.firstNameError = "First Name must be between 2 and 64 characters long";
      }
      if (!lastName) {
        this.lastNameError = "Last Name is required";
      }
      else if (lastName.length < 2 || lastName.length > 64) {
        this.lastNameError = "Last Name must be between 2 and 64 characters long";
      }

      if (this.passwordError || this.passwordConfirmError || this.emailError || this.firstNameError || this.lastNameError) {
        this.processing = false;
        cont = false;
      }
    });

    if (!cont) {
      return;
    }

    try {

      await signOut();
      const [result] = await register({
        email,
        firstName,
        lastName,
        password
      });

      runInAction(() => {
        if (result.error) {
          this.processing = false;
          console.log(result.message);
          this.serverError = result.message;
        }
      });

      if (this.serverError) {
        return;
      }

      const [loginResult] = await signIn({
        username: this.email,
        password
      });

      runInAction(() => {
        this.processing = false;
        if (loginResult.error) {
          changeView("SignUpPartial", { firstName, lastName, email });
        }
        else {
          changeView("SignUpComplete", { firstName, lastName });
        }
      });

    }
    catch (e) {
      console.error(e);
      runInAction(() => {
        this.processing = false;
        this.serverError = "An unknown error occurred while creating your account. Please wait 1 minute and try again.";
      });
    }
  }

}

export const SignUp = observer(({ email: _email }) => {
  const classes = useClasses();
  const [store] = useState(() => new SignUpStore(app, _email));
  const {
    email,
    emailError,
    showEmailHint,
    firstName,
    firstNameError,
    lastName,
    lastNameError,
    password,
    passwordError,
    passwordConfirm,
    passwordConfirmError,
    passwordVisible,
    passwordHintVisible,
    processing,
    serverError
  } = store;

  return (
    <React.Fragment>
      <Box
        className={joinClasses(
          classes._Padding,
          classes._Flex,
          classes._FlexDirectionColumn
        )}
      >
        <Box className={classes._Margin}>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                disabled={processing}
                error={Boolean(firstNameError)}
                value={firstName}
                helperText={firstNameError}
                onChange={action(event => store.firstName = event.target.value)}
                onKeyPress={(e) => { if (e.charCode === 13) { store.createUser() } }}
                onFocus={action(() => {
                  store.serverError = false;
                  store.firstNameError = false;
                })}
                label="First name"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                disabled={processing}
                error={Boolean(lastNameError)}
                value={lastName}
                helperText={lastNameError}
                onChange={action(event => store.lastName = event.target.value)}
                onKeyPress={(e) => { if (e.charCode === 13) { store.createUser() } }}
                onFocus={action(() => {
                  store.serverError = false;
                  store.lastNameError = false;
                })}
                label="Last name"
              />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes._Margin}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            onFocus={action(() => {
              store.serverError = false;
              store.emailError = false;
              store.showEmailHint = true;
            })}
            label="Email"
            value={email}
            error={Boolean(emailError)}
            disabled={processing}
            onChange={action(event => store.email = event.target.value)}
            helperText={emailError}
            onKeyPress={(e) => { if (e.charCode === 13) { store.createUser() } }}
            onBlur={action(() => store.showEmailHint = false)}
          />
        </Box>

        <Collapse in={showEmailHint}>
          <Box className={classes._Margin}>
            <Alert severity="info">
              Please make sure you type the email address correctly
            </Alert>
          </Box>
        </Collapse>

        <Box className={classes._Margin}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Password"
            type={passwordVisible ? "text" : "password"}
            value={password}
            onFocus={action(() => {
              store.serverError = false;
              store.passwordError = false;
              store.passwordHintVisible = true;
            })}
            onChange={action(event => store.password = event.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.createUser() } }}
            onBlur={action(() => store.passwordHintVisible = false)}
            error={Boolean(passwordError)}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={action(() => store.passwordVisible = !passwordVisible)}
                    edge="end"
                    tabIndex="-1"
                  >
                    {passwordVisible ? (<VisibilityOffIcon />) : (<VisibilityIcon />)}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Collapse in={passwordHintVisible}>
          <Box className={classes._Margin}>
            <PasswordHint password={password} />
          </Box>
        </Collapse>

        <Box className={classes._Margin}>
          {" "}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={processing}
            label="Password confirm"
            type="password"
            value={passwordConfirm}
            onFocus={action(() => {
              store.serverError = false;
              store.passwordConfirmError = false;
            })}
            onChange={action(event => store.passwordConfirm = event.target.value)}
            onKeyPress={(e) => { if (e.charCode === 13) { store.createUser() } }}
            error={Boolean(passwordConfirmError)}
            helperText={passwordConfirmError}
          />
        </Box>

        <Collapse in={Boolean(serverError)}>
          <Box mb={2}>
            <Alert severity="error">
              <Typography>{serverError}</Typography>
            </Alert>
          </Box>
        </Collapse>

        <Box className={classes._Margin}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            type="submit"
            startIcon={
              processing ? (
                <AutorenewIcon className={classes.processingIcon} />
              ) : null
            }
            onClick={() => store.createUser()}
            disabled={processing}
          >
            Sign Up
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box className={classes._Padding}>
        <Box className={classes._Margin}>
          <Button
            variant="text"
            fullWidth
            disabled={processing}
            onClick={() => changeView("EmailPasswordLogin", { email })}
            startIcon={<ChevronLeftIcon />}
          >
            Back to Sign in
          </Button>
        </Box>
      </Box>
    </React.Fragment>
  )
})

SignUp.title = "Create Account"